import { BehaviorSubject, map } from 'rxjs';
import { simpleAbilityCheck } from '../ability';
import { UserRole } from '../access';
import { FeaturePlugin } from './feature-plugin.class';
import { checkForRequiredScope } from './feature.utils';
import { FeatureType } from './interfaces';
const defaultFeatureAccessInfo = {
  role: UserRole.GUEST,
  abilities: []
};
/**
 * tests for this class are located at libs\core\frontend-shared\src\lib\feature\feature-registry\feature-status.service.spec.ts!
 * NOTE: All backend plugins are of FeatureType.Permanent. So they will always be enabled as soon as they have been registered.
 */
export class FeatureStatusService {
  constructor() {
    this.featureStatusMap$ = new BehaviorSubject(new Map());
  }
  get(info) {
    const pluginInfo = info instanceof FeaturePlugin ? info.__info : info;
    if (this.featureStatusMap$.getValue().has(pluginInfo.name)) {
      return this.featureStatusMap$.getValue().get(pluginInfo.name);
    } else {
      // default FeatureStatus values
      return this.getDefaultStatus(info);
    }
  }
  get$(info) {
    const pluginInfo = info instanceof FeaturePlugin ? info.__info : info;
    // TODO: introduce change detection filter?
    return this.featureStatusMap$.pipe(map(statusMap => {
      let status = statusMap.get(pluginInfo.name);
      if (!status) status = this.getDefaultStatus(pluginInfo);
      return status;
    }));
  }
  getAll() {
    return this.featureStatusMap$.getValue();
  }
  getAll$() {
    return this.featureStatusMap$.asObservable();
  }
  getDefaultStatus(info) {
    const pluginInfo = info instanceof FeaturePlugin ? info.__info : info;
    return {
      enabled: pluginInfo.type === FeatureType.Permanent ? true : false,
      loaded: false,
      canActivate: false,
      activated: false
    };
  }
  // called by FeatureManagerService + locally
  updateFeatureStatus(info, patch) {
    const pluginInfo = info instanceof FeaturePlugin ? info.__info : info;
    const prev = this.get(pluginInfo);
    const newStatus = {
      ...prev,
      ...patch
    };
    const statusMap = this.featureStatusMap$.getValue();
    statusMap.set(pluginInfo.name, newStatus);
    this.featureStatusMap$.next(statusMap);
    return newStatus;
  }
  // called by FeatureManagerService
  calculateFeatureStatus(pluginInfo, accessInfo, activatedOptInFeatures) {
    const newStatus = {};
    if (pluginInfo.type === FeatureType.Permanent) {
      newStatus.enabled = true;
    } else if (pluginInfo.type === FeatureType.Dynamic) {
      newStatus.enabled = (!pluginInfo.requiredScopes || checkForRequiredScope(pluginInfo, accessInfo.currentScope)) && (!pluginInfo.simpleAbilityCheck || simpleAbilityCheck(accessInfo.role, accessInfo.abilities, pluginInfo.simpleAbilityCheck));
    } else if (pluginInfo.type === FeatureType.OptIn) {
      newStatus.canActivate = !pluginInfo.simpleAbilityCheck || simpleAbilityCheck(accessInfo.role, accessInfo.abilities, pluginInfo.simpleAbilityCheck);
      newStatus.activated = activatedOptInFeatures.includes(pluginInfo.name);
      newStatus.enabled = newStatus.canActivate && newStatus.activated;
    }
    return newStatus;
  }
  calculateFullStatus(pluginInfo, featureAccessInfo, activatedOptInFeatures) {
    const oldStatus = this.get(pluginInfo);
    const newStatus = {
      ...oldStatus,
      ...this.calculateFeatureStatus(pluginInfo, featureAccessInfo, activatedOptInFeatures)
    };
    return newStatus;
  }
  // called by FeatureRegistry
  setNewStatus(feature, featureAccessInfo, activatedOptInFeatures) {
    const newStatus = this.calculateFullStatus(feature.__info, {
      ...defaultFeatureAccessInfo,
      ...featureAccessInfo
    }, activatedOptInFeatures);
    this.updateFeatureStatus(feature, newStatus);
    return newStatus;
  }
}